import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Heart,
    Brain,
    Users,
    Target,
    CheckCircle,
    ArrowRight,
    Sparkles,
    Zap,
    FileText,
    ChevronDown,
    Shield,
    Award,
    Eye,
    Layers,
    MessageCircle,
    Lock,
    Crown
} from 'lucide-react';

const HomePage = () => {
    const navigate = useNavigate();

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Fade-in on scroll
    const observerRef = useRef(null);
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('opacity-100', 'translate-y-0');
                        entry.target.classList.remove('opacity-0', 'translate-y-8');
                    }
                });
            },
            { threshold: 0.1 }
        );
        document.querySelectorAll('.fade-up').forEach((el) => {
            observerRef.current.observe(el);
        });
        return () => observerRef.current?.disconnect();
    }, []);

    return (
        <div className="min-h-screen bg-white">

            {/* ========== HERO ========== */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-32 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                {/* ========== LOGO - LEFT SIDE ========== */}
                <div className="absolute top-28 left-28 z-10 hidden lg:block">
                    <img
                        src="https://affinityclub.agency/wp-content/uploads/2025/12/cropped-AffinityClub-300x300.png"
                        alt="Affinity Club"
                        className="w-56 h-56 xl:w-64 xl:h-64 object-contain drop-shadow-2xl"
                    />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-40 lg:pb-28">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        {/* Left content */}
                        <div className="lg:col-span-7 lg:pl-40">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                                <Sparkles size={18} className="text-indigo-400" />
                                Evaluare psihologică în 4 niveluri progresive
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
                                Cunoaște-te.
                                <br />
                                <span className="text-white">Apoi alege conștient.</span>
                            </h1>

                            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-xl font-light">
                                De la preferințele conștiente până la tiparele profunde care îți ghidează alegerile —
                                descoperă ce te face compatibil și primește o analiză AI personalizată.
                            </p>

                            {/* Standard vs Premium indicator */}
                            <div className="flex flex-wrap gap-3 mb-8">
                                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                                    <CheckCircle size={14} className="text-emerald-400" />
                                    <span className="text-white text-sm">Niveluri 1-2: Standard</span>
                                </div>
                                <div className="flex items-center gap-2 bg-amber-500/20 backdrop-blur-md px-4 py-2 rounded-full border border-amber-500/30">
                                    <Crown size={14} className="text-amber-400" />
                                    <span className="text-amber-300 text-sm">Niveluri 3-4: Premium + AI</span>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => navigate('/teste')}
                                    className="group bg-white text-slate-900 px-8 py-4 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 shadow-xl hover:shadow-white/25 flex items-center justify-center gap-2"
                                >
                                    Abonament Standard
                                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    onClick={() => scrollToSection('niveluri')}
                                    className="text-slate-300 px-8 py-4 rounded-full font-semibold hover:text-white hover:bg-white/10 transition-all border border-white/20 flex items-center justify-center gap-2"
                                >
                                    Explorează nivelurile
                                    <ChevronDown size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Right — visual element */}
                        <div className="lg:col-span-5 hidden lg:block">
                            <div className="relative">
                                <div className="space-y-4">
                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Heart className="text-indigo-400" size={18} />
                                                <span className="text-slate-200 text-sm font-medium">Stil de Atașament</span>
                                            </div>
                                            <span className="bg-emerald-500/20 text-emerald-300 text-xs font-bold px-3 py-1 rounded-full">Securizant</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-1.5">
                                            <div className="bg-emerald-400 h-1.5 rounded-full" style={{ width: '82%' }} />
                                        </div>
                                    </div>

                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 transform -rotate-1 hover:rotate-0 transition-transform duration-500">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Brain className="text-amber-400" size={18} />
                                                <span className="text-slate-200 text-sm font-medium">Maturitate Emoțională</span>
                                            </div>
                                            <span className="text-amber-300 text-sm font-bold">21/24</span>
                                        </div>
                                        <div className="w-full bg-white/10 rounded-full h-1.5">
                                            <div className="bg-amber-400 h-1.5 rounded-full" style={{ width: '88%' }} />
                                        </div>
                                    </div>

                                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 transform rotate-0.5 hover:rotate-0 transition-transform duration-500">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="text-indigo-400" size={18} />
                                                <span className="text-slate-200 text-sm font-medium">Analiză AI</span>
                                            </div>
                                            <span className="bg-amber-500/20 text-amber-300 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                <Crown size={10} /> Premium
                                            </span>
                                        </div>
                                        <p className="text-slate-400 text-xs leading-relaxed italic">
                                            "Profilul tău relațional indică o orientare securizantă cu o capacitate remarcabilă de reglare emoțională..."
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ========== WAVE DIVIDER ========== */}
                <div className="absolute bottom-0 left-0 right-0 leading-none">
                    <svg
                        className="w-full h-16 md:h-20 text-white"
                        viewBox="0 0 1440 80"
                        fill="currentColor"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M0,32L48,42.7C96,53,192,75,288,74.7C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,64C1248,64,1344,64,1392,64L1440,64L1440,80L1392,80C1344,80,1248,80,1152,80C1056,80,960,80,864,80C768,80,672,80,576,80C480,80,384,80,288,80C192,80,96,80,48,80L0,80Z"></path>
                    </svg>
                </div>
            </section>

            {/* ========== STATS BAR ========== */}
            <section className="py-12 px-6 bg-white border-b border-slate-200 -mt-1">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { value: '4', label: 'Niveluri de evaluare', color: 'text-indigo-600' },
                            { value: '16', label: 'Dimensiuni de personalitate', color: 'text-purple-600' },
                            { value: '100%', label: 'Confidențial', color: 'text-emerald-600' },
                            { value: 'AI', label: 'Exclusiv Premium', color: 'text-amber-600' },
                        ].map((stat, i) => (
                            <div key={i} className="text-center py-4">
                                <div className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
                                <div className="text-slate-500 text-sm">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== CELE 4 NIVELURI ========== */}
            <section id="niveluri" className="py-28 px-6 bg-gradient-to-b from-white to-slate-50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-20 fade-up opacity-0 translate-y-8 transition-all duration-700">
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
                            4 niveluri. Un singur scop:{' '}
                            <span className="text-indigo-600">claritate.</span>
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto font-light">
                            Fiecare nivel dezvăluie un strat nou al personalității tale relaționale.
                        </p>

                        {/* Standard vs Premium Legend */}
                        <div className="flex items-center justify-center gap-6 mt-6">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                                <span>Standard (Gratuit)</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                <span>Premium + AI (Acces complet)</span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Nivel 1 - STANDARD */}
                        <div className="fade-up opacity-0 translate-y-8 transition-all duration-700 group">
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all relative overflow-hidden">
                                <div className="absolute top-4 right-4">
                                    <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                        <CheckCircle size={12} /> Standard
                                    </span>
                                </div>

                                <div className="flex items-start gap-6">
                                    <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-lg">
                                        <Users className="text-white" size={26} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="bg-slate-800 text-white px-3 py-0.5 rounded-full text-xs font-bold tracking-wide">NIVEL 1</span>
                                            <span className="text-slate-400 text-sm">4 dimensiuni · 17 întrebări</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">Standarde Extrinseci & Relaționale</h3>
                                        <p className="text-slate-600 mb-4 leading-relaxed">
                                            Ce îți dorești conștient într-o relație: statut economic-social, autonomie vs. sprijin, roluri de gen și stil de viață.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {['Statut Economic-Social', 'Independență vs. Sprijin', 'Roluri de Gen', 'Stil de Viață'].map(d => (
                                                <span key={d} className="bg-slate-50 text-slate-600 px-3 py-1 rounded-lg text-xs font-medium border border-slate-200">{d}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Nivel 2 - STANDARD */}
                        <div className="fade-up opacity-0 translate-y-8 transition-all duration-700 group">
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all relative overflow-hidden">
                                <div className="absolute top-4 right-4">
                                    <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                        <CheckCircle size={12} /> Standard
                                    </span>
                                </div>

                                <div className="flex items-start gap-6">
                                    <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-lg">
                                        <Heart className="text-white" size={26} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="bg-indigo-600 text-white px-3 py-0.5 rounded-full text-xs font-bold tracking-wide">NIVEL 2</span>
                                            <span className="text-slate-400 text-sm">5 dimensiuni · 31 întrebări</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">Standarde Intrinseci</h3>
                                        <p className="text-slate-600 mb-4 leading-relaxed">
                                            Cine ești cu adevărat în relații: atașament, valori profunde, maturitate emoțională, limbaje ale iubirii și inteligență emoțională.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {['Stil de Atașament', 'Valori Relaționale', 'Maturitate Emoțională', 'Limbaje ale Iubirii', 'Inteligență Emoțională'].map(d => (
                                                <span key={d} className="bg-slate-50 text-slate-600 px-3 py-1 rounded-lg text-xs font-medium border border-slate-200">{d}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Nivel 3 - PREMIUM */}
                        <div className="fade-up opacity-0 translate-y-8 transition-all duration-700 group">
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all relative overflow-hidden">
                                <div className="absolute top-4 right-4">
                                    <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                                        <Crown size={12} /> Premium + AI
                                    </span>
                                </div>
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-400/10 to-transparent rounded-bl-full"></div>

                                <div className="flex items-start gap-6">
                                    <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-lg">
                                        <Brain className="text-white" size={26} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="bg-amber-600 text-white px-3 py-0.5 rounded-full text-xs font-bold tracking-wide">NIVEL 3</span>
                                            <span className="text-slate-400 text-sm">7 întrebări profunde</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">Motivații Subconștiente</h3>
                                        <p className="text-slate-600 mb-4 leading-relaxed">
                                            De ce alegi aceleași tipuri de persoane? Acest nivel dezvăluie tiparele relaționale inconștiente și motivațiile adânci.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {['Atragere spre indisponibilitate', 'Complexul salvatorului', 'Atracție față de haos', 'Repetarea relației parentale'].map(d => (
                                                <span key={d} className="bg-white text-slate-600 px-3 py-1 rounded-lg text-xs font-medium border border-amber-200">{d}</span>
                                            ))}
                                        </div>
                                        <div className="mt-4 flex items-center gap-2 text-amber-700 text-sm font-medium">
                                            <Lock size={14} />
                                            <span>Accesibil doar cu pachetul Premium</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Nivel 4 - PREMIUM */}
                        <div className="fade-up opacity-0 translate-y-8 transition-all duration-700 group">
                            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 border-2 border-amber-400 hover:shadow-2xl transition-all relative overflow-hidden">
                                <div className="absolute top-4 right-4">
                                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                                        <Crown size={12} /> Premium + AI
                                    </span>
                                </div>
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-400/20 to-transparent rounded-bl-full"></div>

                                <div className="flex items-start gap-6">
                                    <div className="w-14 h-14 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-lg">
                                        <Target className="text-white" size={26} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="bg-indigo-500 text-white px-3 py-0.5 rounded-full text-xs font-bold tracking-wide">NIVEL 4</span>
                                            <span className="text-slate-400 text-sm">16 dimensiuni · 55 întrebări</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">Trăsături de Personalitate (MCMI)</h3>
                                        <p className="text-slate-400 mb-4 leading-relaxed">
                                            Cel mai profund nivel. 16 dimensiuni de personalitate cu semnale de atenție, vulnerabilități și recomandare de specialist când e cazul.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {['Solitar & Rezervat', 'Altruist & Dedicat', 'Orientat spre Conexiune', 'Încrezător & Ambițios',
                                                'Organizat & Perfecționist', 'Precaut & Sensibil', 'Dominant & Asertiv', 'Expresiv & Sociabil',
                                                'Nonconformist & Aventurier', 'Strategic & Detașat', 'Sceptic & Critic', 'Neconvențional & Creativ',
                                                'Intens & Pasional', 'Vigilent & Precaut', 'Sensibil & Prevăzător', 'Introspectiv & Melancolic'
                                            ].map(d => (
                                                <span key={d} className="bg-white/10 text-slate-300 px-2.5 py-1 rounded-lg text-xs font-medium border border-white/5">{d}</span>
                                            ))}
                                        </div>
                                        <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm font-medium">
                                            <Lock size={14} />
                                            <span>Accesibil doar cu pachetul Premium • Include Analiză AI</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="text-center mt-16">
                        <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                            <button
                                onClick={() => navigate('/teste')}
                                className="group bg-white text-slate-900 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-3"
                            >
                                Abonament Standard
                                <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
                            </button>
                            <button
                                onClick={() => navigate('/pricing')}
                                className="group bg-gradient-to-r from-amber-500 to-orange-500 text-white px-12 py-5 rounded-full font-bold text-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-2xl hover:shadow-amber-500/25 inline-flex items-center gap-3"
                            >
                                <Crown size={20} />
                                Abonament Premium + AI
                            </button>
                        </div>
                        <p className="text-slate-500 text-sm">
                            Nivelurile 1-2 Analiza • Nivelurile 3-4 + Analiză + Ai
                        </p>
                    </div>
                </div>
            </section>

            {/* ========== CUM FUNCȚIONEAZĂ ========== */}
            <section className="py-28 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-20 fade-up opacity-0 translate-y-8 transition-all duration-700">
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
                            Cum funcționează
                        </h2>
                        <p className="text-lg text-slate-600 font-light">Trei pași simpli</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                step: '01',
                                icon: <FileText size={24} />,
                                title: 'Completează testele',
                                desc: 'Răspunde sincer la întrebări. Progresul tău se salvează automat — poți reveni oricând.'
                            },
                            {
                                step: '02',
                                icon: <Sparkles size={24} />,
                                title: 'Primești analiza',
                                desc: 'Standard: rezultate de bază. Premium: analiză AI narativă care interpretează rezultatele tale în detaliu.'
                            },
                            {
                                step: '03',
                                icon: <Eye size={24} />,
                                title: 'Explorează-ți profilul',
                                desc: 'Toate rezultatele sunt într-un profil unificat — tabloul complet al personalității tale relaționale.'
                            }
                        ].map((item, i) => (
                            <div key={i} className="fade-up opacity-0 translate-y-8 transition-all duration-700 text-center">
                                <div className="text-5xl font-bold text-slate-200 mb-4">{item.step}</div>
                                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg text-white">
                                    {item.icon}
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== CE PRIMEȘTI ========== */}
            <section className="py-28 px-6 bg-gradient-to-b from-white to-slate-50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-20 fade-up opacity-0 translate-y-8 transition-all duration-700">
                        <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-6 border border-indigo-100">
                            <Award size={16} />
                            Ce primești
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
                            Nu doar scoruri. <span className="text-indigo-600">Înțelegere.</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: <Layers className="text-indigo-600" size={24} />,
                                title: 'Profil detaliat',
                                desc: 'Scoruri vizuale pe fiecare dimensiune, cu nivele, progres bars și clasificări clare.',
                                bgColor: 'bg-indigo-50',
                                borderColor: 'border-indigo-200',
                                tier: 'Standard + Premium'
                            },
                            {
                                icon: <Sparkles className="text-amber-600" size={24} />,
                                title: 'Analiză AI narativă',
                                desc: 'O analiză scrisă în ton profesional, cald, care îți explică ce înseamnă rezultatele pentru tine.',
                                bgColor: 'bg-amber-50',
                                borderColor: 'border-amber-200',
                                tier: 'Exclusiv Premium',
                                premium: true
                            },
                            {
                                icon: <MessageCircle className="text-emerald-600" size={24} />,
                                title: 'Acces la psihologi',
                                desc: 'Dacă rezultatele indică zone sensibile, poți programa o ședință cu unul din psihologii noștri parteneri.',
                                bgColor: 'bg-emerald-50',
                                borderColor: 'border-emerald-200',
                                tier: 'Standard + Premium'
                            }
                        ].map((item, i) => (
                            <div key={i} className={`fade-up opacity-0 translate-y-8 transition-all duration-700 ${item.bgColor} rounded-2xl p-8 border-2 ${item.borderColor} hover:shadow-xl hover:-translate-y-1 transition-all relative ${item.premium ? 'ring-2 ring-amber-400' : ''}`}>
                                {item.premium && (
                                    <div className="absolute -top-3 -right-3">
                                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                                            <Crown size={10} /> Premium
                                        </span>
                                    </div>
                                )}

                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-5 shadow-md border border-slate-100">
                                    {item.icon}
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                                <div className={`mt-4 text-xs font-medium ${item.premium ? 'text-amber-700' : 'text-slate-500'}`}>
                                    {item.tier}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== CONFIDENȚIALITATE ========== */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-4xl mx-auto">
                    <div className="fade-up opacity-0 translate-y-8 transition-all duration-700 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 rounded-3xl p-8 md:p-12 border border-white/10 shadow-xl">
                        <div className="flex flex-col md:flex-row items-center gap-8">
                            <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                                <Shield className="text-emerald-400" size={32} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-2">Datele tale sunt în siguranță</h3>
                                <p className="text-slate-300 leading-relaxed font-light">
                                    Răspunsurile tale sunt stocate securizat și nu sunt partajate cu nimeni. Analiza AI se generează exclusiv pentru tine.
                                    Poți șterge toate datele oricând din profilul tău.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== CTA FINAL ========== */}
            <section className="py-28 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white relative overflow-hidden">
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="relative max-w-3xl mx-auto text-center">
                    <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tight">
                        Ești gata să te cunoști cu adevărat?
                    </h2>
                    <p className="text-xl md:text-2xl text-slate-300 mb-10 font-light max-w-2xl mx-auto">
                        Fiecare răspuns te apropie de tine. Fiecare analiză îți arată o cale nouă.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                        <button
                            onClick={() => navigate('/teste')}
                            className="group bg-white text-slate-900 px-10 py-4 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 shadow-xl hover:shadow-white/25 inline-flex items-center gap-2"
                        >
                            Abonament Standard
                            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="group bg-gradient-to-r from-amber-500 to-orange-500 text-white px-10 py-4 rounded-full font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-xl hover:shadow-amber-500/25 inline-flex items-center gap-2"
                        >
                            <Crown size={18} />
                            Abonament Premium + AI
                        </button>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
                        <div className="flex items-center gap-2">
                            <Shield size={18} className="text-emerald-400" />
                            <span>Confidențial</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Zap size={18} className="text-emerald-400" />
                            <span>Progres salvat</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Sparkles size={18} className="text-amber-400" />
                            <span>AI exclusiv Premium</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;